// Layout.jsx - Memory Leak Safe Version - COMPLETE
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header/Header';
import Sidebar from './Sidebar/Sidebar';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [endpoints, setEndpoints] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const isEndpointsPage = location.pathname.startsWith('/endpoints');
  
  // Refs for cleanup tracking
  const cleanupFunctionsRef = useRef(new Set());
  const eventListenersRef = useRef(new Map());
  const timeoutsRef = useRef(new Set());
  const intervalsRef = useRef(new Set());

  // Helper to register cleanup functions
  const registerCleanup = useCallback((cleanupFn) => {
    cleanupFunctionsRef.current.add(cleanupFn);
    return () => {
      cleanupFunctionsRef.current.delete(cleanupFn);
    };
  }, []);

  // Safe event listener registration
  const addEventListenerSafe = useCallback((target, event, handler, options) => {
    try {
      target.addEventListener(event, handler, options);
      
      // Store for cleanup
      const key = `${event}_${Date.now()}_${Math.random()}`;
      eventListenersRef.current.set(key, { target, event, handler, options });
      
      // Return cleanup function
      return () => {
        try {
          target.removeEventListener(event, handler, options);
          eventListenersRef.current.delete(key);
        } catch (error) {
          console.warn('Error removing event listener:', error);
        }
      };
    } catch (error) {
      console.error('Error adding event listener:', error);
      return () => {}; // Return no-op cleanup
    }
  }, []);

  // Safe timeout registration
  const setTimeoutSafe = useCallback((callback, delay) => {
    const timeoutId = setTimeout(() => {
      timeoutsRef.current.delete(timeoutId);
      callback();
    }, delay);
    
    timeoutsRef.current.add(timeoutId);
    
    return () => {
      clearTimeout(timeoutId);
      timeoutsRef.current.delete(timeoutId);
    };
  }, []);

  // Debounced endpoint update to prevent excessive re-renders
  const updateEndpointsDebounced = useCallback(
    (() => {
      let timeoutId;
      return (newEndpoints) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        
        timeoutId = setTimeout(() => {
          setEndpoints(prevEndpoints => {
            // Avoid unnecessary state updates
            if (JSON.stringify(prevEndpoints) === JSON.stringify(newEndpoints)) {
              return prevEndpoints;
            }
            return newEndpoints || [];
          });
        }, 100);
        
        // Register cleanup
        registerCleanup(() => {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        });
      };
    })(),
    [registerCleanup]
  );

  // Listen for real endpoint data from children components
  useEffect(() => {
    const handleEndpointsUpdate = (event) => {
      try {
        if (event.detail && Array.isArray(event.detail)) {
          updateEndpointsDebounced(event.detail);
        }
      } catch (error) {
        console.error('Error handling endpoint update:', error);
      }
    };

    // Register event listener safely
    const removeListener = addEventListenerSafe(window, 'updateEndpoints', handleEndpointsUpdate);
    
    // Cleanup function
    return () => {
      removeListener();
    };
  }, [addEventListenerSafe, updateEndpointsDebounced]);

  // Fallback: Extract endpoints from collection data if available
  useEffect(() => {
    // Avoid memory leaks by checking if component is still mounted
    let isMounted = true;
    
    const extractFallbackEndpoints = () => {
      try {
        // Only use fallback if no endpoints received from child components
        if (endpoints.length === 0 && location.state?.collection?.data?.paths) {
          const collection = location.state.collection;
          const extractedEndpoints = [];

          Object.entries(collection.data.paths).forEach(([path, methods]) => {
            Object.entries(methods).forEach(([method, methodData]) => {
              extractedEndpoints.push({
                name: methodData.summary || `${method.toUpperCase()} ${path}`,
                url: path,
                method: method.toUpperCase(),
                description: methodData.description,
                operationId: methodData.operationId,
                tags: methodData.tags || [],
                path: path,
                summary: methodData.summary
              });
            });
          });

          if (isMounted) {
            updateEndpointsDebounced(extractedEndpoints);
          }
        }
      } catch (error) {
        console.error('Error extracting fallback endpoints:', error);
      }
    };

    // Use timeout to prevent blocking
    const timeoutId = setTimeout(extractFallbackEndpoints, 0);
    
    // Cleanup function
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [location.state, endpoints.length, updateEndpointsDebounced]);

  // Handle endpoint selection from search with navigation safety
  const handleEndpointSelect = useCallback((endpoint) => {
    try {
      if (!endpoint || typeof endpoint !== 'object') {
        return;
      }

      // Dispatch custom event for EndpointList to listen
      const event = new CustomEvent('selectEndpoint', {
        detail: endpoint
      });
      window.dispatchEvent(event);
      
      // Safe navigation
      if (location.pathname !== '/endpoints') {
        // Use navigate instead of window.location for SPA navigation
        navigate('/endpoints', { 
          state: { selectedEndpoint: endpoint },
          replace: false 
        });
      }
    } catch (error) {
      console.error('Error handling endpoint selection:', error);
    }
  }, [location.pathname, navigate]);

  // Safe sidebar toggle with debouncing
  const handleSidebarToggle = useCallback(
    (() => {
      let lastToggle = 0;
      return (isOpen) => {
        const now = Date.now();
        // Prevent rapid toggling
        if (now - lastToggle < 100) {
          return;
        }
        lastToggle = now;
        setSidebarOpen(isOpen);
      };
    })(),
    []
  );

  // Handle sidebar close with escape key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && sidebarOpen) {
        handleSidebarToggle(false);
      }
    };

    if (sidebarOpen) {
      const removeListener = addEventListenerSafe(document, 'keydown', handleKeyDown);
      return removeListener;
    }
  }, [sidebarOpen, handleSidebarToggle, addEventListenerSafe]);

  // Cleanup all resources on unmount
  useEffect(() => {
    return () => {
      try {
        timeoutsRef.current.forEach(id => clearTimeout(id));
        timeoutsRef.current.clear();

        intervalsRef.current.forEach(id => clearInterval(id));
        intervalsRef.current.clear();

        eventListenersRef.current.forEach(({ target, event, handler, options }) => {
          try { target.removeEventListener(event, handler, options); } catch {}
        });
        eventListenersRef.current.clear();

        cleanupFunctionsRef.current.forEach(fn => {
          try { fn(); } catch {}
        });
        cleanupFunctionsRef.current.clear();
      } catch {}
    };
  }, []);


  return (
    <div className="flex h-screen bg-primary">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => handleSidebarToggle(false)} 
      />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header with Search */}
        <Header 
          onMenuClick={() => handleSidebarToggle(true)}
          endpoints={endpoints}
          onEndpointSelect={handleEndpointSelect}
        />
        
        {/* Page Content */}
        <main className={`flex-1 overflow-auto bg-secondary ${isEndpointsPage ? 'p-0' : 'p-6'}`}>
          <div className={isEndpointsPage ? 'max-w-none' : 'max-w-7xl mx-auto'}>
            {children}
          </div>
        </main>
      </div>
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => handleSidebarToggle(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleSidebarToggle(false);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Kenar çubuğunu kapat"
        />
      )}
    </div>
  );
};

// Export with React.memo for performance optimization
export default React.memo(Layout);
